import { FC, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';

interface ChatInputProps {
  disabled?: boolean;
  onSend: (content: string) => void | Promise<void>;
  placeholder?: string;
}

const MAX_HEIGHT_PX = 200;

const ChatInput: FC<ChatInputProps> = ({ disabled, onSend, placeholder }) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = value.trim();

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  }

  // Cresce/encolhe conforme o usuário digita (e ao limpar após enviar).
  useEffect(resizeTextarea, [value]);

  async function submit() {
    if (!trimmed || disabled) return;
    const payload = trimmed;
    setValue('');
    await onSend(payload);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border-t border-surface-border bg-surface-primary p-4">
      <div className="flex gap-2 items-end">
        <textarea
          ref={textareaRef}
          data-testid="chat-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          placeholder={placeholder || 'Pergunte algo…'}
          style={{ maxHeight: `${MAX_HEIGHT_PX}px` }}
          className="flex-1 resize-none overflow-y-auto rounded-xl bg-surface-secondary border border-surface-border px-4 py-3 text-content-primary placeholder:text-content-tertiary focus:border-accent focus:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={!trimmed || disabled}
          className="h-12 px-5 rounded-xl bg-accent text-surface-primary font-semibold hover:bg-accent/90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span className="material-symbols-outlined">send</span>
          Enviar
        </button>
      </div>
      <p className="mt-2 text-2xs text-content-tertiary">
        Enter envia · Shift+Enter quebra linha
      </p>
    </form>
  );
};

export default ChatInput;
